import * as React from "react"
import { useParams, Link, useNavigate, useOutletContext } from "react-router-dom"
import dayjs from "dayjs"
import { useQueryClient } from "@tanstack/react-query"

// components
import { PrimeButton } from "components-design-system"
import Modal from "components/Modal/Modal"
import { Panel } from "components-design-system"
import RequestAllFields from "./RequestAllFields/RequestAllFields"
import FilesGridTools from "containers/files/components/FilesGrid/FilesGridTools/FilesGridTools"
import FilesGrid from "containers/files/components/FilesGrid/FilesGrid"
import RequestFieldPanelWrapper from "containers/requests/components/fields/RequestFieldPanelWrapper/RequestFieldPanelWrapper"
import WorkflowTracker from "components/WorkflowTracker/WorkflowTracker"
import TrackerHeader from "components/WorkflowTracker/TrackerHeader/TrackerHeader"
import RequestFaveButton from "containers/requests/Request/RequestFaveButton/RequestFaveButton"

// styles
import "./RequestOverview.scss"

// apis
import { useGetRequest, usePutRequest, usePostStudyDataSync } from "api/hooks/requests/useRequestsApi"
import { useUserPermissions } from "api/hooks"
import queryKeys from "api/utils/queryKeys"

// context
import useWorkflowTracker from "context/hooks/useWorkflowTracker"

const RequestOverview = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { requestId } = useParams()
  const { projectedCompletionDate } = useWorkflowTracker()

  // server state
  const { data: request } = useGetRequest(requestId)
  const putRequest = usePutRequest(request?.id)
  const postStudyDataSync = usePostStudyDataSync(request?.id)
  const { data: permissions } = useUserPermissions()

  // local state
  const { onOpenPanel, breadcrumbsData, requestFiles, setAddFilesModalOpen } = useOutletContext()
  const [requestFormData, setRequestFormData] = React.useState(null)
  const [selectedStudies, setSelectedStudies] = React.useState([])
  const [search, setSearch] = React.useState("")
  const [editInfoModalOpen, setEditInfoModalOpen] = React.useState(false)
  const [isEditStepModalOpenInOverview, setIsEditStepModalOpenInOverview] = React.useState(false)

  // set default request info data
  React.useEffect(() => {
    if (request) {
      setRequestFormData(request)
      if (request.corestudies) {
        setSelectedStudies(request.corestudies.map((study) => study.id))
      }
    }
    return () => {
      setRequestFormData(null)
    }
  }, [request, editInfoModalOpen])

  const onSelectionChanged = ({ api }) => {
    const selectedNodeData = api.getSelectedNodes().map((node) => node.data)
    console.log("Bulk remove files from request", selectedNodeData)
  }

  const onChangeRequest = (key, val) => {
    const keys = key.split(".")
    const fieldKey = keys.pop()
    let tempData = { ...requestFormData }
    const obj = keys.reduce((nestedObj, nestedKey) => nestedObj[nestedKey], tempData)
    obj[fieldKey] = val

    setRequestFormData(tempData)
  }

  const onSaveChanges = () => {
    putRequest(
      {
        body: {
          request_data: {
            ...requestFormData.request_data,
            custom_data: requestFormData.request_data?.custom_data || {},
          },
          due_date: requestFormData.due_date,
          request_category_id: requestFormData.request_category_id,
          study_db_ids: selectedStudies,
          projected_completion_date: dayjs(projectedCompletionDate).format("YYYY-MM-DD"),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.workflows.detail(request?.workflow_id),
            filters: {},
            options: { model: "request" },
          })
          setEditInfoModalOpen(false)
        },
      }
    )
  }

  return (
    <>
      <TrackerHeader
        model="request"
        title={request?.request_id}
        status={request?.status}
        breadcrumbs={breadcrumbsData}
        workflowId={request?.workflow_id}
        workflowObj={request}
        onOpenPanel={onOpenPanel}
        showWorkFlowActions={false}
        actions={[<RequestFaveButton request={request} />]}
      />
      <div className="request-overview">
        <Panel
          title="REQUEST INFO"
          iconClassName="fal fa-info-circle"
          actions={
            <PrimeButton
              icon={<i className="fal fa-edit" />}
              tooltip={permissions?.["request.edit"] ? null : "You do not have sufficient permissions to edit requests"}
              onClick={() => setEditInfoModalOpen(true)}
              disabled={!permissions?.["request.edit"]}
              className="request-action-btn"
            >
              Edit info
            </PrimeButton>
          }
          className="request-info-panel"
        >
          <RequestFieldPanelWrapper
            request={request}
            onClick={permissions?.["request.edit"] ? () => setEditInfoModalOpen(true) : null}
            disabled={!permissions?.["request.edit"]}
          />
        </Panel>

        <Panel
          title="WORKFLOW"
          iconClassName="fal fa-project-diagram"
          actions={
            <PrimeButton
              icon={<i className="fal fa-arrow-right" />}
              onClick={() => navigate(`/request/${requestId}/workflow`)}
              className="request-action-btn"
            >
              Go to workflow
            </PrimeButton>
          }
          className="request-workflow-panel"
        >
          {/* Workflow */}
          <WorkflowTracker
            model={"request"}
            showSettings={false}
            showDays={true}
            showDueDates={false}
            overrideSettings={true}
            readOnly={true}
            workflowId={request?.workflow_id}
            isEditStepModalOpenInOverview={isEditStepModalOpenInOverview}
          />
        </Panel>

        <Panel
          title={<Link to={`/request/${requestId}/files`}>{requestFiles?.length} FILES</Link>}
          iconClassName="fal fa-file-alt"
          actions={
            <FilesGridTools
              setSearch={setSearch}
              setAddFilesModalOpen={setAddFilesModalOpen}
            />
          }
          className="request-files-panel"
        >
          <FilesGrid
            rowData={requestFiles}
            search={search}
            rowMultiSelectWithClick={true}
            onSelectionChanged={onSelectionChanged}
          />
        </Panel>
      </div>
      <Modal
        type="edit"
        title="Edit request info"
        open={editInfoModalOpen}
        onCancel={() => setEditInfoModalOpen(false)}
        width="80vw"
        okText="Save"
        onOk={onSaveChanges}
        className="request-edit-modal"
        footer={[]}
      >
        <RequestAllFields
          requestFormData={requestFormData}
          onChangeRequest={onChangeRequest}
          selectedStudies={selectedStudies}
          setSelectedStudies={setSelectedStudies}
        />
        <div className="actions">
          <PrimeButton
            key="cancel-btn"
            className="close-btn"
            onClick={() => setEditInfoModalOpen(false)}
          >
            Close
          </PrimeButton>
          <PrimeButton
            key="ok-btn"
            type="primary"
            className="ok-btn"
            onClick={onSaveChanges}
          >
            Save
          </PrimeButton>
          <PrimeButton
            icon={<i className="fal fa-sync" />}
            key="ok-btn-sync"
            type="primary"
            className="ok-btn"
            disabled={request?.corestudies?.length === 0}
            tooltip={
              request?.corestudies?.length === 0
                ? "Study data can only be synced after a study has been linked and saved to the request."
                : "NOTE: Any unsaved data will be removed.  Replace existing values for Product ID(s), Product name(s), Therapeutic area(s), Indication(s), Program name(s) with current values in linked studies."
            }
            onClick={() => postStudyDataSync({})}
          ></PrimeButton>
        </div>
      </Modal>
    </>
  )
}

export default RequestOverview
